'use client';

import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/Card';

export interface Post {
  id: string;
  title: string;
  summary: string;
  label: string;
  author: string;
  published: string;
  url: string;
  image: string;
}

export interface Blog7Props {
  tagline: string;
  heading: string;
  description: string;
  buttonText: string;
  buttonUrl: string;
  posts: Post[];
}

const defaultPosts: Post[] = [
  {
    id: 'post-1',
    title: 'Getting Started with EnthiosHub',
    summary:
      'Learn how to create and manage events in minutes. We cover setup, ticketing, and check-in so you can run smooth events from day one.',
    label: 'Tutorial',
    author: 'Sarah Chen',
    published: '1 Jan 2024',
    url: '/blog',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&q=80&w=800&h=450',
  },
  {
    id: 'post-2',
    title: 'Building Accessible Event Experiences',
    summary:
      'Explore how to create inclusive events using clear signage, accessible venues, and inclusive registration options.',
    label: 'Accessibility',
    author: 'Marcus Rodriguez',
    published: '1 Jan 2024',
    url: '/blog',
    image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&q=80&w=800&h=450',
  },
  {
    id: 'post-3',
    title: 'Event Analytics That Matter',
    summary:
      'Dive into metrics that help you understand attendance, engagement, and revenue so you can improve your next event.',
    label: 'Analytics',
    author: 'Emma Thompson',
    published: '1 Jan 2024',
    url: '/blog',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800&h=450',
  },
  {
    id: 'post-4',
    title: 'QR Check-in Best Practices',
    summary:
      'Speed up entry and reduce queues with QR code check-in. Tips for printing, scanning, and handling edge cases.',
    label: 'Tips',
    author: 'Alex Kim',
    published: '5 Jan 2024',
    url: '/blog',
    image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&q=80&w=800&h=450',
  },
  {
    id: 'post-5',
    title: 'Scaling From 100 to 2000 Attendees',
    summary:
      'How to grow your event without breaking your workflow. Lessons from organizers who scaled successfully.',
    label: 'Growth',
    author: 'Priya Sharma',
    published: '10 Jan 2024',
    url: '/blog',
    image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&q=80&w=800&h=450',
  },
  {
    id: 'post-6',
    title: 'Hybrid Events: In-Person and Online',
    summary:
      'Run sessions that work for both in-person and remote attendees. Tools and formats that keep everyone engaged.',
    label: 'Hybrid',
    author: 'David Park',
    published: '15 Jan 2024',
    url: '/blog',
    image: 'https://images.unsplash.com/photo-1591115765373-5207764f72e7?auto=format&fit=crop&q=80&w=800&h=450',
  },
  // Duplicate-style entries to see grid layout with more cards
  {
    id: 'post-7',
    title: 'Getting Started with EnthiosHub',
    summary:
      'Learn how to create and manage events in minutes. We cover setup, ticketing, and check-in so you can run smooth events from day one.',
    label: 'Tutorial',
    author: 'Sarah Chen',
    published: '20 Jan 2024',
    url: '/blog',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&q=80&w=800&h=450',
  },
  {
    id: 'post-8',
    title: 'Event Analytics That Matter',
    summary:
      'Dive into metrics that help you understand attendance, engagement, and revenue so you can improve your next event.',
    label: 'Analytics',
    author: 'Emma Thompson',
    published: '25 Jan 2024',
    url: '/blog',
    image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&q=80&w=800&h=450',
  },
  {
    id: 'post-9',
    title: 'QR Check-in Best Practices',
    summary:
      'Speed up entry and reduce queues with QR code check-in. Tips for printing, scanning, and handling edge cases.',
    label: 'Tips',
    author: 'Alex Kim',
    published: '1 Feb 2024',
    url: '/blog',
    image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&q=80&w=800&h=450',
  },
];

export function Blog7({
  tagline = 'Latest Updates',
  heading = 'Blog Posts',
  description = 'Discover the latest trends, tips, and best practices for running events. From check-in to analytics, stay updated with our expert insights.',
  buttonText = 'View all articles',
  buttonUrl = '/blog',
  posts = defaultPosts,
}: Blog7Props) {
  return (
    <section className="py-32 bg-landing-bg">
      <div className="container mx-auto flex flex-col items-center gap-16 lg:px-16">
        <div className="text-center">
          <Badge variant="secondary" className="mb-6 border-nature-border text-nature-muted">
            {tagline}
          </Badge>
          <h2 className="mb-3 text-pretty text-3xl font-semibold text-landing-text md:mb-4 md:text-4xl lg:mb-6 lg:max-w-3xl lg:text-5xl">
            {heading}
          </h2>
          <p className="mb-8 text-landing-muted md:text-base lg:max-w-2xl lg:text-lg">
            {description}
          </p>
          <Link href={buttonUrl}>
            <Button variant="link" className="w-full sm:w-auto">
              {buttonText}
              <ArrowRight className="ml-2 size-4" />
            </Button>
          </Link>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {posts.map((post) => (
            <Card key={post.id} variant="solid" padding="none" hover className="grid grid-rows-[auto_auto_1fr_auto] overflow-hidden">
              <div className="aspect-[16/9] w-full relative">
                <Link href={post.url} className="block transition-opacity duration-200 hover:opacity-70">
                  <Image
                    src={post.image}
                    alt={post.title}
                    width={800}
                    height={450}
                    className="h-full w-full object-cover object-center"
                  />
                </Link>
              </div>
              <CardHeader className="p-6 pb-0">
                <h3 className="text-lg font-semibold text-landing-text hover:underline md:text-xl">
                  <Link href={post.url}>{post.title}</Link>
                </h3>
              </CardHeader>
              <CardContent className="p-6 pt-3">
                <p className="text-landing-muted">{post.summary}</p>
              </CardContent>
              <CardFooter className="p-6 pt-0">
                <Link
                  href={post.url}
                  className="flex items-center text-landing-text hover:underline"
                >
                  Read more
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
